import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  addCustomerQuotation,
  fetchCustomerNotificationCount,
  fetchCustomerNotifications,
  fetchCustomerQuotations,
  fetchCustomers,
  fetchOneCustomer,
} from "../../api/customer.api";
import { ICustomer } from "../../models/epcCustomer.model";
import { IQuotation } from "../../models/quotationData.model";

interface CustomerState {
  customers: ICustomer[];
  loading: boolean;
  customer: ICustomer;
  error: string | null;
  quotations: { [key: string]: IQuotation[] };
  quotationsLoading: boolean;
  quotationsError: string | null;
  notifications: {
    [days: number]: ICustomer[];
  };
  notificationCount?: number;
}

const initialState: CustomerState = {
  customers: [],
  loading: false,
  customer: {} as ICustomer,
  error: null,
  quotations: {},
  quotationsLoading: false,
  quotationsError: null,
  notifications: {},
};

// Thunk to fetch customers
export const getAllCustomers = createAsyncThunk<ICustomer[]>(
  "customers/fetchCustomers",
  async () => {
    const response = await fetchCustomers();
    return response;
  }
);

export const getCustomerNotifications = createAsyncThunk<
  { days: number; customers: ICustomer[] },
  number
>("customers/fetchCustomerNotifications", async (days: number) => {
  const data = await fetchCustomerNotifications(days);
  return { days, customers: data };
});

export const getCustomerNotificationCount = createAsyncThunk(
  "customers/getCustomerNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCustomerNotificationCount();
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

// Thunk to fetch customer quotations
export const getCustomerQuotations = createAsyncThunk(
  "customers/fetchCustomerQuotations",
  async (customerId: string) => {
    const response = await fetchCustomerQuotations(customerId);
    return response;
  }
);

export const getOneCustomer = createAsyncThunk(
  "customers/fetchOneCustomer",
  async (customerId: string) => {
    const response = await fetchOneCustomer(customerId);
    return response;
  }
);
export const addCustomersQuotation = createAsyncThunk(
  "customers/addCustomerQuotation",
  async ({
    customerId,
    leadId,
    goods,
    transportPrice,
  }: {
    customerId: string;
    leadId: string;
    goods: any;
    transportPrice: number;
  }) => {
    const response = await addCustomerQuotation(
      customerId,
      leadId,
      goods,
      transportPrice
    );
    return response.data;
  }
);

const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    addCustomer: (state, action: PayloadAction<ICustomer>) => {
      state.customers.push(action.payload);
    },
    removeCustomer: (state, action: PayloadAction<number>) => {
      state.customers = state.customers.filter(
        (customer) => customer.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(getAllCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch customers";
      })
      .addCase(getCustomerNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const { days, customers } = action.payload;
        state.notifications[days] = customers;
      })
      .addCase(getCustomerNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch customer notifications";
      })
      .addCase(getCustomerNotificationCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerNotificationCount.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationCount = action.payload;
      })
      .addCase(getCustomerNotificationCount.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch notification count";
      })
      .addCase(getCustomerQuotations.pending, (state) => {
        state.quotationsLoading = true;
        state.quotationsError = null;
      })
      .addCase(getCustomerQuotations.fulfilled, (state, action) => {
        state.quotationsLoading = false;
        state.quotations = action.payload;
      })
      .addCase(getCustomerQuotations.rejected, (state, action) => {
        state.quotationsLoading = false;
        state.quotationsError =
          action.error.message || "Failed to fetch quotations";
      })
      .addCase(getOneCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOneCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customer = action.payload;
      })
      .addCase(getOneCustomer.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const { addCustomer, removeCustomer } = customerSlice.actions;

export default customerSlice.reducer;
